/**
 * useConfiguration — загружает/сохраняет конфигурацию каналов/интеграций/модулей сделки.
 * GET  /api/deals/{id}/configuration
 * PUT  /api/deals/{id}/configuration
 *
 * В DEV возвращает пустую конфигурацию без сетевого запроса.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { configApi, gateApi } from '@/api/client'
import { dealKeys } from '@/hooks/useDeals'
import type { Configuration, ConfigurationResponse } from '@/types'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

const EMPTY_CONFIG_RESPONSE: ConfigurationResponse = {
  configuration: null,
  calc_price: null,
}

export const configKeys = {
  detail: (dealId: string) => ['configuration', dealId] as const,
}

export function useConfiguration(dealId: string) {
  return useQuery({
    queryKey: configKeys.detail(dealId),
    queryFn: async (): Promise<ConfigurationResponse> => {
      if (USE_MOCK) return EMPTY_CONFIG_RESPONSE
      return configApi.get(dealId)
    },
    enabled: !!dealId,
  })
}

export function useSaveConfiguration(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Configuration): Promise<ConfigurationResponse> => {
      if (USE_MOCK) {
        // Mock: echo back with a minimal calc_price so UI can show result
        const mockResp: ConfigurationResponse = {
          configuration: body,
          calc_price: {
            base: 60000,
            extra_channels: 0,
            extra_channels_breakdown: [],
            contract_total: 60000,
            first_tranche: 30000,
            second_tranche: 30000,
            upsells_total: 0,
            upsells_breakdown: [],
            monthly_upsells: 0,
            monthly_upsells_breakdown: [],
            subscription_base: 4900,
            subscription_forecast: {
              dialogs: body.dialogs_forecast || 0,
              base: 4900,
              overflow: 0,
              total: 4900,
              breakdown: [],
            },
            breakdown: [
              { label: 'Базовый пакет (1 канал)', amount: 60000 },
            ],
          },
        }
        return Promise.resolve(mockResp)
      }
      return configApi.put(dealId, body)
    },
    onSuccess: (data) => {
      // Update configuration cache directly
      qc.setQueryData(configKeys.detail(dealId), data)
      // Invalidate deal so events list / value updates (правильные ключи dealKeys)
      qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) })
      qc.invalidateQueries({ queryKey: dealKeys.events(dealId) })
    },
  })
}

/**
 * useMarkGate — ручное подтверждение Ворот 1 (созвон назначен).
 * POST /api/deals/{id}/gate  {gate_key, passed}
 * После успеха обновляет карточку сделки (gates) и ленту событий.
 */
export function useMarkGate(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ gateKey, passed }: { gateKey: string; passed: boolean }) => {
      if (USE_MOCK) return Promise.resolve({ gate_key: gateKey, result: passed ? 'passed' : 'pending' })
      return gateApi.mark(dealId, gateKey, passed)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealKeys.detail(dealId) })
      qc.invalidateQueries({ queryKey: dealKeys.events(dealId) })
    },
  })
}
